import { useCallback, useEffect, useRef, useState } from "react";
import { useSidebar } from "@/components/ui/sidebar";
import {
  clampSidebarWidth,
  emitSidebarWidthChange,
  readSavedSidebarWidth,
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  saveSidebarWidth,
} from "@/lib/sidebar-width";

const KEYBOARD_STEP = 10; // px per arrow key press
type ResizeSide = "left" | "right";

/**
 * Find the closest sidebar-wrapper ancestor and update its --sidebar-width CSS variable.
 * The shadcn Sidebar reads this variable for both the spacer (sidebar-gap) and the
 * fixed-position container (sidebar-container), keeping them in sync automatically.
 */
function getWrapper(element: HTMLElement | null) {
  return element?.closest<HTMLElement>('[data-slot="sidebar-wrapper"]') ?? null;
}

function setSidebarWidth(element: HTMLElement | null, width: number) {
  const wrapper =
    getWrapper(element) ?? document.querySelector<HTMLElement>('[data-slot="sidebar-wrapper"]');
  if (wrapper) {
    wrapper.style.setProperty("--sidebar-width", `${width}px`);
  }
}

function clampWidth(width: number, minWidth: number, maxWidth: number) {
  return Math.min(maxWidth, Math.max(minWidth, Math.round(width)));
}

function getWidthFromPointer(clientX: number, side: ResizeSide) {
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
  return side === "left" ? clientX : viewportWidth - clientX;
}

function getKeyboardAdjustedWidth(
  side: ResizeSide,
  currentWidth: number,
  key: string,
  minWidth: number,
  maxWidth: number
) {
  switch (key) {
    case "ArrowLeft":
      return side === "left"
        ? Math.max(minWidth, currentWidth - KEYBOARD_STEP)
        : Math.min(maxWidth, currentWidth + KEYBOARD_STEP);
    case "ArrowRight":
      return side === "left"
        ? Math.min(maxWidth, currentWidth + KEYBOARD_STEP)
        : Math.max(minWidth, currentWidth - KEYBOARD_STEP);
    case "Home":
      return minWidth;
    case "End":
      return maxWidth;
    default:
      return null;
  }
}

interface EdgeResizeHandleProps {
  side: ResizeSide;
  currentWidth: number;
  minWidth: number;
  maxWidth: number;
  ariaLabel: string;
  onWidthChange: (width: number) => void;
  onWidthCommit: (width: number) => void;
  resolveResizeContainer?: (element: HTMLElement | null) => HTMLElement | null;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
  testId?: string;
}

export function EdgeResizeHandle({
  side,
  currentWidth,
  minWidth,
  maxWidth,
  ariaLabel,
  onWidthChange,
  onWidthCommit,
  resolveResizeContainer,
  onResizeStart,
  onResizeEnd,
  testId,
}: EdgeResizeHandleProps) {
  const handleRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      isDragging.current = true;

      const handle = handleRef.current;
      if (!handle) return;

      handle.setPointerCapture(e.pointerId);
      const resizeContainer = resolveResizeContainer?.(handle) ?? null;
      resizeContainer?.setAttribute("data-resizing", "");
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      onResizeStart?.();

      const onPointerMove = (moveEvent: PointerEvent) => {
        if (!isDragging.current) return;
        const rawWidth = getWidthFromPointer(moveEvent.clientX, side);
        onWidthChange(clampWidth(rawWidth, minWidth, maxWidth));
      };

      const onPointerUp = (upEvent: PointerEvent) => {
        isDragging.current = false;
        handle.releasePointerCapture(upEvent.pointerId);
        resizeContainer?.removeAttribute("data-resizing");
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        onResizeEnd?.();

        const rawWidth = getWidthFromPointer(upEvent.clientX, side);
        onWidthCommit(clampWidth(rawWidth, minWidth, maxWidth));

        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [
      maxWidth,
      minWidth,
      onResizeEnd,
      onResizeStart,
      onWidthChange,
      onWidthCommit,
      resolveResizeContainer,
      side,
    ]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const newWidth = getKeyboardAdjustedWidth(side, currentWidth, e.key, minWidth, maxWidth);
      if (newWidth === null) return;
      e.preventDefault();
      onWidthChange(newWidth);
      onWidthCommit(newWidth);
    },
    [currentWidth, maxWidth, minWidth, onWidthChange, onWidthCommit, side]
  );

  return (
    /* biome-ignore lint/a11y/useSemanticElements: This is a keyboard-accessible resize handle with a custom hit area, not a visual rule. */
    <div
      ref={handleRef}
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={currentWidth}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      tabIndex={0}
      data-testid={testId}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      className={[
        "absolute top-0 bottom-0 w-1 z-20 cursor-col-resize group/resize-handle focus-visible:outline-none",
        side === "left" ? "-right-px" : "-left-px",
      ].join(" ")}
    >
      <div className="absolute inset-y-0 -left-1 -right-1 transition-colors group-focus-visible/resize-handle:bg-primary/10 group-focus-visible/resize-handle:ring-2 group-focus-visible/resize-handle:ring-ring" />
    </div>
  );
}

/**
 * SidebarResizeHandle - Drag handle on the right edge of the left sidebar.
 *
 * Positioned inside the Sidebar component's fixed container. On drag, updates
 * the --sidebar-width CSS variable on the SidebarProvider wrapper, which the
 * shadcn Sidebar uses for both its spacer div and fixed container width.
 *
 * Only renders on desktop when the sidebar is expanded.
 */
export function SidebarResizeHandle() {
  const { open, isMobile } = useSidebar();
  const [currentWidth, setCurrentWidth] = useState(readSavedSidebarWidth());

  // Restore saved width on mount
  useEffect(() => {
    if (isMobile) return;
    const saved = readSavedSidebarWidth();
    setCurrentWidth(saved);
    if (saved !== SIDEBAR_DEFAULT_WIDTH) {
      setSidebarWidth(null, saved);
    }
    emitSidebarWidthChange(saved);
  }, [isMobile]);

  // Only show on desktop when sidebar is expanded
  if (!open || isMobile) return null;

  return (
    <EdgeResizeHandle
      side="left"
      currentWidth={currentWidth}
      minWidth={SIDEBAR_MIN_WIDTH}
      maxWidth={SIDEBAR_MAX_WIDTH}
      ariaLabel="Resize sidebar"
      resolveResizeContainer={getWrapper}
      onWidthChange={(width) => {
        const nextWidth = clampSidebarWidth(width);
        setSidebarWidth(null, nextWidth);
        setCurrentWidth(nextWidth);
        emitSidebarWidthChange(nextWidth);
      }}
      onWidthCommit={(width) => {
        const finalWidth = clampSidebarWidth(width);
        setSidebarWidth(null, finalWidth);
        setCurrentWidth(finalWidth);
        saveSidebarWidth(finalWidth);
        emitSidebarWidthChange(finalWidth);
      }}
      testId="left-sidebar-resize-handle"
    />
  );
}
